import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function LeadForm({ plotInterest, compact = false }) {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    message: "",
    plot_interest: plotInterest || "",
  });
  const [loading, setLoading] = useState(false);

  const handle = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please provide your name and phone number.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/leads", {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || null,
        message: form.message.trim() || null,
        plot_interest: form.plot_interest.trim() || null,
      });
      toast.success("Thank you! Our team will contact you shortly.");
      setForm({ name: "", phone: "", email: "", message: "", plot_interest: plotInterest || "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Submission failed. Please try again.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-[#0f0f11] border border-[#2a2a2d] rounded-md px-4 py-3 text-sm text-white placeholder:text-[#6f6f74] focus:border-[#c5a059] focus:outline-none transition";

  return (
    <form
      onSubmit={submit}
      data-testid="lead-form"
      className={`space-y-4 ${compact ? "" : "p-8 lg:p-10 bg-[#0f0f11] border border-[#2a2a2d] rounded-xl"}`}
    >
      {!compact && (
        <div className="mb-2">
          <p className="label-overline">Register for Updates</p>
          <h3 className="font-serif text-3xl md:text-4xl mt-2">Be the first to know when we launch.</h3>
          <p className="text-[#8f8f94] text-sm mt-2">Share your details and we'll keep you posted on launch dates and layout information. No booking, no obligation.</p>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-4">
        <input
          data-testid="lead-input-name"
          className={inputCls}
          placeholder="Full Name *"
          value={form.name}
          onChange={handle("name")}
          required
        />
        <input
          data-testid="lead-input-phone"
          className={inputCls}
          placeholder="Phone Number *"
          value={form.phone}
          onChange={handle("phone")}
          required
        />
      </div>

      <input
        data-testid="lead-input-email"
        type="email"
        className={inputCls}
        placeholder="Email Address"
        value={form.email}
        onChange={handle("email")}
      />

      <input
        data-testid="lead-input-plot"
        className={inputCls}
        placeholder="Plot size of interest (e.g., 1500 sqft, East-facing)"
        value={form.plot_interest}
        onChange={handle("plot_interest")}
      />

      <textarea
        data-testid="lead-input-message"
        className={inputCls + " min-h-[110px] resize-none"}
        placeholder="Any specific question or preference?"
        value={form.message}
        onChange={handle("message")}
      />

      <button
        type="submit"
        disabled={loading}
        data-testid="lead-submit-btn"
        className="btn-gold w-full py-3.5 rounded-md text-sm font-medium tracking-wide inline-flex items-center justify-center gap-2 disabled:opacity-60"
      >
        {loading && <Loader2 size={16} className="animate-spin" />}
        {loading ? "Submitting…" : "Register for Launch Updates"}
      </button>

      <p className="text-[11px] text-[#6f6f74] text-center">
        We respect your privacy. Your details are used solely for follow-up by our sales team.
      </p>
    </form>
  );
}
